import {Component, Input, OnInit} from '@angular/core';
import {GtNotificationService} from '@app/services/gt-notification.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {environment} from '@environment';
import {NameValueListType} from '@app/services/gt-configuration.service';
import {StatusModel} from '@app/models/status.model';
import {StatusAvailableModel} from '@app/models/status-available.model';
import {GtStatusModelService} from '@app/services/gt-status-model.service';
import {GtSynchronizationService} from '@app/services/gt-synchronization.service';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {BehaviorSubject, EMPTY} from 'rxjs';
import {GtAbsenceService} from '@app/services/gt-absence.service';

export enum FWD_SETTINGS_CATEGORY {
  BUSINESS,
  OUT_OF_OFFICE,
  PRIVATE
}

@Component({
  selector: 'gt-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss']
})
export class UserSettingsComponent implements OnInit {
  @Input() showForwarding = true;
  @Input() showStatus = true;
  @Input() showAdvancedSettings = false;
  @Input() login: string;
  @Input() orgname: string;
  @Input() canEdit = true;
  @Input() fullNumber: string;
  @Input() showTwinning = false;
  nameValueListTypes = NameValueListType;
  isAdministrator = false;
  showEWSSync = false;
  hasTwinningNum = false;
  showBlockOutgoing = environment.hasBlockOutgoingOption;
  absenceReasons = new BehaviorSubject<StatusModel[]>([]);
  absenceReasons$ = this.absenceReasons.asObservable()  ;

  currentState = State.CHOOSE_SETTING;
  State = State;

  selectedStatus: StatusModel;
  statusAvailable: StatusAvailableModel;

  FWD_SETTINGS_CATEGORY = FWD_SETTINGS_CATEGORY;
  selectedFwdSettingsCategory = FWD_SETTINGS_CATEGORY.BUSINESS;

  constructor(
    private notificationService: GtNotificationService,
    private authenticationService: GtAuthenticationService,
    private directoryService: GtDirectoryService,
    private absenceService: GtAbsenceService,
    private statusModelService: GtStatusModelService,
    private synchronizationService: GtSynchronizationService
  ) {}

  ngOnInit() {
    if (!this.orgname) {
      this.orgname = this.authenticationService.oname;
    }
    if (!this.login) {
      this.login = this.authenticationService.login;
    }
    this.directoryService
      .getEndUserNumbers(this.orgname, this.login)
      .pipe(tap(r => (this.hasTwinningNum = r.length > 1)))
      .subscribe();
    this.authenticationService
      .isAdmin()
      .pipe(
        tap(isAdministrator => (this.isAdministrator = isAdministrator)),
        switchMap(() => this.absenceService.getAbsenceReasons(this.orgname, this.login)),
        switchMap(() => this.statusModelService.statusModels$(this.orgname, this.login)),
        tap(statusModels => {
          this.statusAvailable = statusModels.find(r => r.reasonId === 0) as StatusAvailableModel;
          this.absenceReasons.next(statusModels.filter(r => r.reasonId !== 0));
        }),
        switchMap(() => this.synchronizationService.getSyncMembers(this.orgname, this.login)),
        tap(members => {
          if (members && members.some(member => member.login === this.login)) {
            this.showEWSSync = true;
          }
        }),
        catchError(error => {
          this.error(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private error = error => {
    this.notificationService.errorNotification.next(error);
  };

  changeSelectedStatus(status: StatusModel) {
    this.selectedStatus = status;
    this.changeState(State.STATUS_SETTINGS);
  }

  changeState(newState: State) {
    this.currentState = newState;
  }
}

export enum State {
  CHOOSE_SETTING,
  CHOOSE_FORWARDING_SETTINGS,
  FORWARDING,
  DISPLAY_NUMBER,
  WORKING_HOURS,
  STATUS_SETTINGS,
  SELECT_STATUS,
  ADVANCED,
  BLACKLIST,
  PRIORITIZED_NUMBERS,
  EWS_SYNC,
  OUTGOING_CALLS,
  TWINNING_SETTINGS
}
